import { FC, useCallback, useMemo, useState } from "react";

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import { buildCombinationKey, cartesianProduct, ShopVariantType } from "@simplyk/common";
import { Control, FieldError, useFormContext, useFormState, UseFormSetValue, useWatch } from "react-hook-form";
import { v4 as uuid } from "uuid";

import { StepTicketsFormData } from "../../types";
import { MAX_VARIANTS_PER_RATE, ShopVariantPreset } from "../helpers/shopVariantPresets";
import { ShopVariantData, VariantSeatData } from "../types";

import { InventoryRow } from "./ShopInventoryModal/helper";
import { ShopInventoryModal } from "./ShopInventoryModal/ShopInventoryModal";
import { ShopVariantEditor } from "./ShopVariantEditor/ShopVariantEditor";
import { ShopVariantTypeSelector } from "./ShopVariantTypeSelector/ShopVariantTypeSelector";

import { useTranslate } from "@/common/hooks/useTranslate";
import { DashboardButton } from "@/dashboard-design-system/DashboardButton";
import { DashboardTooltip } from "@/dashboard-design-system/DashboardTooltip";
import { Add, Lists } from "@/icons/outlined";

interface ShopVariantsProps {
  control: Control<StepTicketsFormData>;
  setValue: UseFormSetValue<StepTicketsFormData>;
  rateIndex: number;
  rateName: string;
  isInventoryOpen: boolean;
  onInventoryOpenChange: (open: boolean) => void;
}

export const ShopVariants: FC<ShopVariantsProps> = ({
  control,
  setValue,
  rateIndex,
  rateName,
  isInventoryOpen,
  onInventoryOpenChange,
}) => {
  const { t } = useTranslate();
  const [isSelectingType, setIsSelectingType] = useState(false);

  const shopVariantsPath = `rates.${rateIndex}.shopVariants` as const;
  const shopVariantsToDeletePath = `rates.${rateIndex}.shopVariantsToDelete` as const;
  const variantSeatsPath = `rates.${rateIndex}.variantSeats` as const;

  const rateSeatsPath = `rates.${rateIndex}.seats` as const;

  const alreadyBoughtTicketsCountPath = `rates.${rateIndex}.alreadyBoughtTicketsCount` as const;

  const shopVariants = useWatch({ control, name: shopVariantsPath }) as ShopVariantData[] | undefined;
  const shopVariantsToDelete = useWatch({ control, name: shopVariantsToDeletePath }) as string[] | undefined;
  const variantSeats = useWatch({ control, name: variantSeatsPath }) as VariantSeatData[] | undefined;
  const rateSeats = useWatch({ control, name: rateSeatsPath }) as number | null | undefined;
  const alreadyBoughtTicketsCount = (useWatch({ control, name: alreadyBoughtTicketsCountPath }) as number) ?? 0;

  const activeVariants = useMemo(() => shopVariants || [], [shopVariants]);
  const canAddMore = activeVariants.length < MAX_VARIANTS_PER_RATE;
  const hasVariants = activeVariants.length > 0 && activeVariants.some((v) => v.values.length > 0);

  const purchasedValueIds = useMemo(() => {
    const ids = new Set<string>();
    for (const entry of variantSeats ?? []) {
      if (entry.soldCount > 0) {
        for (const valueId of entry.combinationKey.split(",")) {
          ids.add(valueId);
        }
      }
    }
    return ids;
  }, [variantSeats]);
  const hasVariantPurchases = purchasedValueIds.size > 0;
  const deleteDisabledTooltip = hasVariantPurchases
    ? t("dashboard", "ticketing.variant.deleteDisabledTooltip")
    : undefined;
  const addDisabledTooltip = hasVariantPurchases ? t("dashboard", "ticketing.variant.addDisabledTooltip") : undefined;

  const valuesRequiredMessage = t("dashboard", "ticketing.variant.error.valuesRequired");

  const { register, trigger } = useFormContext<StepTicketsFormData>();
  const { errors, submitCount } = useFormState({ control, name: shopVariantsPath });

  activeVariants.forEach((_, index) => {
    register(`${shopVariantsPath}.${index}.values`, {
      validate: (value) => {
        const valuesArr = value as unknown as { id: string }[] | undefined;
        if (valuesArr === undefined) {
          return true;
        }
        if (valuesArr.length === 0) {
          return valuesRequiredMessage;
        }
        return true;
      },
    });
  });

  const handleSelectPreset = useCallback(
    (preset: ShopVariantPreset) => {
      const defaultValues = (preset.defaultValues ?? []).map((valueName, index) => ({
        id: uuid(),
        name: valueName,
        sortIndex: index,
      }));
      const newVariant: ShopVariantData = {
        id: uuid(),
        name: t("dashboard", preset.labelKey),
        sortIndex: activeVariants.length,
        type: preset.type,
        values: defaultValues,
      };
      setValue(shopVariantsPath, [...activeVariants, newVariant], { shouldDirty: true });
      setIsSelectingType(false);
    },
    [activeVariants, setValue, shopVariantsPath, t]
  );

  const handleCreateFromScratch = useCallback(() => {
    const newVariant: ShopVariantData = {
      id: uuid(),
      name: "",
      sortIndex: activeVariants.length,
      type: ShopVariantType.Custom,
      values: [],
    };
    setValue(shopVariantsPath, [...activeVariants, newVariant], { shouldDirty: true });
    setIsSelectingType(false);
  }, [activeVariants, setValue, shopVariantsPath]);

  const handleUpdateVariant = useCallback(
    (variantIndex: number, updatedVariant: ShopVariantData) => {
      const updatedVariants = activeVariants.map((variant, index) =>
        index === variantIndex ? updatedVariant : variant
      );
      setValue(shopVariantsPath, updatedVariants, { shouldDirty: true });
      if (submitCount > 0) {
        void trigger(`${shopVariantsPath}.${variantIndex}.values`);
      }
    },
    [activeVariants, setValue, shopVariantsPath, trigger, submitCount]
  );

  const handleDeleteVariant = useCallback(
    (variantIndex: number) => {
      const variantToDelete = activeVariants[variantIndex];
      const updatedVariants = activeVariants
        .filter((_, index) => index !== variantIndex)
        .map((variant, index) => ({ ...variant, sortIndex: index }));

      setValue(shopVariantsPath, updatedVariants, { shouldDirty: true });
      setValue(shopVariantsToDeletePath, [...(shopVariantsToDelete || []), variantToDelete.id], {
        shouldDirty: true,
      });
    },
    [activeVariants, setValue, shopVariantsPath, shopVariantsToDeletePath, shopVariantsToDelete]
  );

  const handleAddVariantClick = useCallback(() => {
    setIsSelectingType(true);
  }, []);

  const variantDataByKey = useMemo(() => {
    const map = new Map<string, { seats: number | null; soldCount: number }>();
    for (const entry of variantSeats ?? []) {
      map.set(entry.combinationKey, { seats: entry.seats, soldCount: entry.soldCount });
    }
    return map;
  }, [variantSeats]);

  const inventoryRows: InventoryRow[] = useMemo(() => {
    if (!hasVariants) {
      const available = rateSeats !== null && rateSeats !== undefined ? rateSeats - alreadyBoughtTicketsCount : null;
      return [
        {
          id: "parent",
          name: rateName || "",
          unitsSold: alreadyBoughtTicketsCount,
          availableUnits: available,
        },
      ];
    }

    const valueSets = activeVariants.map((variant) =>
      variant.values.map((value) => ({ id: value.id, name: value.name }))
    );
    const combinations = cartesianProduct(valueSets);

    return combinations.map((combination) => {
      const key = buildCombinationKey(combination.map((v) => v.id));
      const displayName = combination.map((v) => v.name).join(" • ");
      const data = variantDataByKey.get(key);
      const soldCount = data?.soldCount ?? 0;
      const available = data?.seats !== null && data?.seats !== undefined ? data.seats - soldCount : null;
      return {
        id: key,
        name: displayName,
        unitsSold: soldCount,
        availableUnits: available,
      };
    });
  }, [hasVariants, activeVariants, variantDataByKey, rateName, rateSeats, alreadyBoughtTicketsCount]);

  const handleInventorySave = useCallback(
    (rows: InventoryRow[]) => {
      if (!hasVariants) {
        const row = rows[0];
        const seats =
          row?.availableUnits !== null && row?.availableUnits !== undefined ? row.availableUnits + row.unitsSold : null;
        setValue(rateSeatsPath, seats, { shouldDirty: true });
        return;
      }

      const newVariantSeats: VariantSeatData[] = rows.map((row) => ({
        combinationKey: row.id,
        seats: row.availableUnits !== null ? row.availableUnits + row.unitsSold : null,
        soldCount: row.unitsSold,
      }));
      setValue(variantSeatsPath, newVariantSeats, { shouldDirty: true });
    },
    [hasVariants, setValue, rateSeatsPath, variantSeatsPath]
  );

  const variantEditors = activeVariants.map((variant, index) => {
    const variantErrors = errors.rates?.[rateIndex]?.shopVariants?.[index] as { values?: FieldError } | undefined;
    const errorMessage = typeof variantErrors?.values?.message === "string" ? variantErrors.values.message : undefined;
    return (
      <ShopVariantEditor
        key={variant.id}
        variant={variant}
        onChange={(updatedVariant) => handleUpdateVariant(index, updatedVariant)}
        onDelete={() => handleDeleteVariant(index)}
        errorMessage={errorMessage}
        deleteDisabledTooltip={deleteDisabledTooltip}
        purchasedValueIds={purchasedValueIds}
        valueDeleteDisabledTooltip={t("dashboard", "ticketing.variant.valueDeleteDisabledTooltip")}
      />
    );
  });

  const hasAnyVariant = activeVariants.length > 0;

  const manageInventoryButton = (fullWidth: boolean) => (
    <DashboardButton
      variant="outlined"
      vibe="neutral"
      size="small"
      startIcon={<Lists />}
      onClick={() => onInventoryOpenChange(true)}
      data-test="manage-inventory-button"
      fullWidth={fullWidth}
    >
      {t("dashboard", "ticketing.inventory.manageInventory")}
    </DashboardButton>
  );

  const addVariantButton = (fullWidth: boolean) =>
    canAddMore && !isSelectingType ? (
      <DashboardTooltip title={addDisabledTooltip} disabled={!addDisabledTooltip}>
        <Box component="span" sx={fullWidth ? { display: "block" } : undefined}>
          <DashboardButton
            variant={hasAnyVariant ? "text" : "outlined"}
            vibe="neutral"
            size="small"
            startIcon={<Add />}
            onClick={handleAddVariantClick}
            fullWidth={fullWidth}
            disabled={Boolean(addDisabledTooltip)}
            data-test="shop-variant-add-button"
          >
            {hasAnyVariant
              ? t("dashboard", "ticketing.variant.addAnotherVariant")
              : t("dashboard", "ticketing.variant.addVariant")}
          </DashboardButton>
        </Box>
      </DashboardTooltip>
    ) : null;

  const variantTypeSelector = (
    <ShopVariantTypeSelector
      onSelect={handleSelectPreset}
      onCreateFromScratch={handleCreateFromScratch}
      onCancel={() => setIsSelectingType(false)}
    />
  );

  return (
    <Stack spacing={2}>
      {hasAnyVariant ? (
        <>
          <Box>{manageInventoryButton(false)}</Box>
          <Stack spacing={2}>{variantEditors}</Stack>
          {isSelectingType && variantTypeSelector}
          <Box>{addVariantButton(false)}</Box>
        </>
      ) : isSelectingType ? (
        variantTypeSelector
      ) : (
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
          {canAddMore && <Box sx={{ flex: 1, minWidth: 0 }}>{addVariantButton(true)}</Box>}
          <Box sx={{ flex: 1, minWidth: 0 }}>{manageInventoryButton(true)}</Box>
        </Stack>
      )}

      <ShopInventoryModal
        open={isInventoryOpen}
        onClose={() => onInventoryOpenChange(false)}
        rateName={rateName || t("dashboard", "ticketing.inventory.defaultRateName")}
        rows={inventoryRows}
        onSave={handleInventorySave}
      />
    </Stack>
  );
};
